import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";

interface ChoiceProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size" | "className" | "id"> {
  /**
   * Always visible: clicking it toggles the box, and it is the control's
   * accessible name (unless the caller passes an `aria-label`, which must then
   * start with this text so the visible name stays inside the spoken one).
   */
  label: ReactNode;
  /**
   * A second, smaller line under the label. It DESCRIBES the control
   * (`aria-describedby`) and is NOT part of its name — and, sitting outside the
   * `<label>`, it is not a click target either, so a stray click on a long
   * explanation cannot flip a setting (one of them deploys real infrastructure).
   */
  description?: ReactNode;
  /** Classes for the wrapper (spacing, a frame around the choice) — never for the box itself. */
  className?: string;
}

const Choice = forwardRef<HTMLInputElement, ChoiceProps & { type: "checkbox" | "radio" }>(
  function Choice({ type, label, description, className = "", ...rest }, ref) {
    const id = useId();
    const descId = `${id}-description`;
    const describedBy =
      [description ? descId : undefined, rest["aria-describedby"]]
        .filter(Boolean)
        .join(" ") || undefined;
    return (
      <div className={`flex items-start text-sm ${className}`}>
        <input
          {...rest}
          ref={ref}
          id={id}
          type={type}
          aria-describedby={describedBy}
          className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600"
        />
        {/* The gap between the box and its text is the label's own padding, not
            a flex gap: a gap belongs to nothing, and the click between the two
            would land on nothing. The description shares the same indent. */}
        <div>
          <label
            htmlFor={id}
            className={`block pl-2 ${
              rest.disabled ? "cursor-not-allowed text-ink-muted" : "cursor-pointer text-ink"
            }`}
          >
            {label}
          </label>
          {description && (
            <span id={descId} className="block pl-2 text-xs text-ink-muted">
              {description}
            </span>
          )}
        </div>
      </div>
    );
  },
);

/** A value that belongs to a form: it is saved with the form, not on click. */
export const Checkbox = forwardRef<HTMLInputElement, ChoiceProps>(function Checkbox(
  props,
  ref,
) {
  return <Choice {...props} ref={ref} type="checkbox" />;
});

/** One choice among several that share a `name`; wrap the set in a `Fieldset`. */
export const Radio = forwardRef<HTMLInputElement, ChoiceProps>(function Radio(props, ref) {
  return <Choice {...props} ref={ref} type="radio" />;
});

/**
 * A checkbox drawn as a toggle chip, for a short list of named options (the
 * enabled options of an environment, the allowed instance models). The real
 * checkbox is visually hidden — 1px and clipped, so the global focus ring has
 * nothing to draw on — and the chip draws that ring itself (`focus-ring-has`)
 * whenever the checkbox inside has keyboard focus.
 */
export function ChoiceChip({
  label,
  checked,
  onChange,
  mono,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
  mono?: boolean;
}) {
  return (
    <label
      className={`focus-ring-has cursor-pointer rounded-md border px-3 py-1 text-sm ${
        mono ? "font-mono" : ""
      } ${
        checked
          ? "border-brand-500 bg-brand-50 text-brand-700"
          : "border-line-field text-ink hover:bg-sunken"
      }`}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

/**
 * An on / off setting that takes effect the moment it is toggled (no form to
 * save): a checkbox would promise the opposite. `label` is the accessible name;
 * put visible text beside it if the state needs explaining.
 */
export function Switch({
  checked,
  onCheckedChange,
  label,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? "bg-success-dot" : "bg-line-field"
      }`}
    >
      <span
        aria-hidden="true"
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-surface shadow-sm transition-transform ${
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}
